import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { MAX_IMAGES_PER_ITEM } from '@sellit/shared';
import { ApiError, uploadPhoto } from '../lib/api';
import { compressImage } from '../lib/compressImage';
import { Icon } from '../lib/Icon';

export interface Photo {
  key: string;
  /** Set for photos picked on this device; absent for photos already on the item. */
  file?: File;
  previewUrl: string;
  status: 'waiting' | 'uploading' | 'done' | 'error';
  progress: number;
  imageId?: string;
  error?: string;
}

const PARALLEL_UPLOADS = 2;
const MAX_ATTEMPTS = 3;

/** A photo that's already stored (when editing an item). */
export const storedPhoto = (imageId: string, thumbUrl: string): Photo => ({
  key: imageId,
  imageId,
  previewUrl: thumbUrl,
  status: 'done',
  progress: 1,
});

/**
 * Photo list with background uploads: each photo is compressed and uploaded the moment it's
 * added, two at a time, with retries on bad signal.
 */
export function usePhotoUploads(saleId: string, initial: Photo[] = []) {
  const [photos, setPhotos] = useState<Photo[]>(initial);
  const active = useRef(0);
  const queue = useRef<Photo[]>([]);

  const update = (id: string, patch: Partial<Photo>) =>
    setPhotos((list) => list.map((p) => (p.key === id ? { ...p, ...patch } : p)));

  const pump = useCallback(() => {
    while (active.current < PARALLEL_UPLOADS && queue.current.length) {
      const photo = queue.current.shift()!;
      active.current++;
      void (async () => {
        update(photo.key, { status: 'uploading', progress: 0, error: undefined });
        try {
          const blob = await compressImage(photo.file!);
          for (let attempt = 1; ; attempt++) {
            try {
              const res = await uploadPhoto(saleId, blob, (f) => update(photo.key, { progress: f }));
              update(photo.key, { status: 'done', progress: 1, imageId: res.imageId });
              break;
            } catch (err) {
              const retryable = err instanceof ApiError && (err.status === 0 || err.status >= 500);
              if (!retryable || attempt >= MAX_ATTEMPTS) throw err;
              await new Promise((r) => setTimeout(r, 1000 * attempt));
            }
          }
        } catch (err) {
          update(photo.key, { status: 'error', error: err instanceof ApiError ? err.message : 'Upload failed.' });
        } finally {
          active.current--;
          pump();
        }
      })();
    }
  }, [saleId]);

  const addFiles = useCallback(
    (files: File[]) => {
      setPhotos((current) => {
        const room = MAX_IMAGES_PER_ITEM - current.length;
        const added = files.slice(0, Math.max(0, room)).map<Photo>((file) => ({
          key: crypto.randomUUID(),
          file,
          previewUrl: URL.createObjectURL(file),
          status: 'waiting',
          progress: 0,
        }));
        queue.current.push(...added);
        setTimeout(pump, 0);
        return [...current, ...added];
      });
    },
    [pump],
  );

  /** Swaps the cover (first) photo for a new one, keeping any others. */
  const replaceCover = useCallback(
    (file: File) => {
      setPhotos(([old, ...rest]) => {
        if (old) {
          queue.current = queue.current.filter((p) => p.key !== old.key);
          if (old.file) URL.revokeObjectURL(old.previewUrl);
        }
        const photo: Photo = { key: crypto.randomUUID(), file, previewUrl: URL.createObjectURL(file), status: 'waiting', progress: 0 };
        queue.current.push(photo);
        setTimeout(pump, 0);
        return [photo, ...rest];
      });
    },
    [pump],
  );

  function retry(photo: Photo) {
    queue.current.push(photo);
    update(photo.key, { status: 'waiting', error: undefined });
    pump();
  }

  function remove(photo: Photo) {
    queue.current = queue.current.filter((p) => p.key !== photo.key);
    if (photo.file) URL.revokeObjectURL(photo.previewUrl);
    setPhotos((list) => list.filter((p) => p.key !== photo.key));
  }

  function makeCover(photo: Photo) {
    setPhotos((list) => [photo, ...list.filter((p) => p.key !== photo.key)]);
  }

  const reset = useCallback((next: Photo[] = []) => {
    queue.current = [];
    setPhotos((list) => {
      list.forEach((p) => p.file && URL.revokeObjectURL(p.previewUrl));
      return next;
    });
  }, []);

  // Free preview memory when the screen closes.
  const latest = useRef(photos);
  latest.current = photos;
  useEffect(() => () => latest.current.forEach((p) => p.file && URL.revokeObjectURL(p.previewUrl)), []);

  return {
    photos,
    addFiles,
    replaceCover,
    retry,
    remove,
    makeCover,
    reset,
    uploading: photos.some((p) => p.status === 'waiting' || p.status === 'uploading'),
    failed: photos.some((p) => p.status === 'error'),
    imageIds: () => photos.map((p) => p.imageId!),
  };
}

export type PhotoUploads = ReturnType<typeof usePhotoUploads>;

/** Reads picked files from an <input type=file> and clears it so the same photo can be picked again. */
export function takeFiles(e: ChangeEvent<HTMLInputElement>): File[] {
  const files = Array.from(e.target.files ?? []);
  e.target.value = '';
  return files;
}

export function PhotoPicker({ uploads, error }: { uploads: PhotoUploads; error?: string }) {
  const { photos } = uploads;
  return (
    <section aria-label="Photos">
      <div className="photo-grid">
        {photos.map((p, i) => (
          <div key={p.key} className={`photo ${p.status}`}>
            <img src={p.previewUrl} alt="" />
            {p.status === 'uploading' && <span className="progress" style={{ width: `${Math.round(p.progress * 100)}%` }} />}
            {p.status === 'error' && (
              <button type="button" className="photo-error" onClick={() => uploads.retry(p)} title={p.error}>
                Retry
              </button>
            )}
            {photos.length > 1 &&
              (i === 0 ? (
                <span className="photo-cover">Cover</span>
              ) : (
                <button type="button" className="photo-cover button" onClick={() => uploads.makeCover(p)}>
                  Make cover
                </button>
              ))}
            <button type="button" className="photo-remove" aria-label="Remove photo" onClick={() => uploads.remove(p)}>
              ×
            </button>
          </div>
        ))}
      </div>
      {photos.length < MAX_IMAGES_PER_ITEM && (
        <div className="actions two-up">
          <label className="btn">
            <Icon name="camera" /> {photos.length ? 'Another photo' : 'Take photo'}
            <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => uploads.addFiles(takeFiles(e))} />
          </label>
          <label className="btn">
            <Icon name="image" /> Choose photos
            <input type="file" accept="image/*" multiple hidden onChange={(e) => uploads.addFiles(takeFiles(e))} />
          </label>
        </div>
      )}
      {uploads.failed && <p className="error small">{photos.find((p) => p.status === 'error')?.error} Tap Retry or remove it.</p>}
      {error && <p className="error small">{error}</p>}
    </section>
  );
}
